/**
 * Portal API client — separate axios instance + in-memory access token.
 *
 * Why separate from admin's lib/api.js?
 *  - The portal session lives on a different cookie path (/api/portal/startup)
 *    and a different audience claim. Mixing them risks one session refreshing
 *    the wrong audience.
 *  - When admin and portal are both open in the same browser, they should be
 *    able to coexist without leaking state.
 */
import axios from "axios";
import silentRefresh from "@/lib/silentRefresh";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const PORTAL_API = `${BACKEND_URL}/api/portal/startup`;

let _portalAccessToken = null;
const REFRESH_FAILED_EVENT = "wit:portal-refresh-failed";
export const PORTAL_REFRESH_FAILED = REFRESH_FAILED_EVENT;

export function setPortalAccessToken(token) {
  _portalAccessToken = token || null;
}

export function getPortalAccessToken() {
  return _portalAccessToken;
}

export const portalApi = axios.create({
  baseURL: PORTAL_API,
  timeout: 20000,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

portalApi.interceptors.request.use((config) => {
  if (_portalAccessToken) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${_portalAccessToken}`;
  }
  return config;
});

let _refreshing = null;
portalApi.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    if (
      status === 401 &&
      !original._isRetry &&
      !original.url?.includes("/session/refresh") &&
      !original.url?.includes("/magic-link")
    ) {
      try {
        if (!_refreshing) {
          _refreshing = silentRefresh(`${PORTAL_API}/session/refresh`).finally(() => {
            setTimeout(() => { _refreshing = null; }, 0);
          });
        }
        const data = await _refreshing;
        if (!data?.access_token) throw new Error("refresh failed");
        setPortalAccessToken(data.access_token);
        original._isRetry = true;
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${data.access_token}`;
        return portalApi(original);
      } catch (_e) {
        setPortalAccessToken(null);
        window.dispatchEvent(new Event(REFRESH_FAILED_EVENT));
      }
    }
    return Promise.reject(error);
  },
);

export default portalApi;
